import React from "react";
import {
  CssBaseline,
  Box,
  Typography,
  Grid,
  Link,
  useTheme,
} from "@mui/material";
import { LandingPageDescriptionBoxes } from "../components/LandingPageDescriptionBoxes/LandingPageDescriptionBoxes.tsx";
import { cityLandingPageInfo } from "../data/fixedCityLocations.ts";
import { SelectVenuesButton } from "../components/Buttons/SelectVenuesButton.tsx";
import { TitleElement } from "../components/TitleElement/TitleElement.tsx";
import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";

export const GenericLandingPage: React.FC = () => {
  const theme = useTheme();

  return (
    <>
      <CssBaseline />
      <StandardPageHead
        title="CinemaGuide: Tickets & Showtimes For Every Film Showing in Ireland"
        description="Browse movie showtimes & tickets for every cinema in Ireland. Quickly find out what's on at 70+ Irish cinemas."
        pageUrl="https://cinemaguideireland.com"
      />
      <Box
        component="main"
        sx={{
          bgcolor: "background.default",
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
          mb: { xs: 2, md: 2 },
        }}
      >
        <TitleElement
          headerOneText="Plan your next cinema trip."
          headerTwoText="View every Irish cinema showtime in one place."
        />
        <SelectVenuesButton />
      </Box>

      <LandingPageDescriptionBoxes
        locationText="Ireland"
        boxOneHeader="70+ Irish Cinemas"
        boxOneText="View showtimes for every cinema in Ireland. Discover new cinemas."
        boxTwoHeader="Purchase Cinema Tickets"
        boxTwoText="Buy tickets to watch any film showing in Ireland. Pay no extra fees. Book tickets fast."
        boxThreeHeader="1000+ Daily Cinema Showtimes"
        boxThreeText="See every film showing near you. Compare different showtimes. Get the cheapest cinema tickets."
      />

      <Typography
        variant="h3"
        component="h3"
        gutterBottom
        sx={{
          marginLeft: { xs: 0.5, md: 2 },
          marginRight: { xs: 0.5, md: 2 },
          paddingTop: { xs: 1, md: 1 },
          marginTop: 4,
          fontSize: { xs: "1.5rem", md: "2rem" },
          fontWeight: 500,
          textDecoration: "underline",
          maxWidth: "100%",
          textAlign: "center",
        }}
      >
        {`View Films Showing in Popular Locations`}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          mt: { xs: 4, md: 6 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 1.5, sm: 2, md: 4 }}
          sx={{ maxWidth: { xs: "100%", md: "80%" } }}
        >
          {cityLandingPageInfo.map(({ locationName, slug }) => (
            <Grid
              key={slug}
              item
              xs={6}
              sm={3}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Link
                component="a"
                color={theme.palette.primary.main}
                sx={{
                  mb: 2,
                  fontSize: { xs: "large", md: "x-large" },
                  "&:hover": { cursor: "pointer" },
                  textShadow: "0 0 4px rgba(0,160,255,.4)",
                }}
                href={`/${slug}`}
              >
                {locationName}
              </Link>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box
        sx={{
          py: { xs: 2, md: 3 },
          mx: { xs: 1, md: 3 },
          textAlign: "center",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h5"
          component="p"
          gutterBottom
          color="text.secondary"
          sx={{
            marginBottom: { xs: 1, md: 3 },
            mt: { xs: 0, md: 2 },
            paddingLeft: { md: 10 },
            paddingRight: { md: 10 },
            marginRight: { md: 5 },
            marginLeft: { md: 5 },
            fontSize: { xs: "small", md: "large" },
          }}
        >
          {`We have film listings for chains such as Odeon, Vue, IMC, Omniplex, Movies@, The Arc, and every independent cinema...`}
        </Typography>
      </Box>
      <noscript>
        <div>
          CinemaGuide requires JavaScript to display the latest showtimes.
          Please enable JavaScript in your browser.
        </div>
      </noscript>
    </>
  );
};
